import {
	Accordion,
	StarIcon,
	StarOutlineIcon,
	Tooltip,
	UserRatingIcon,
} from "@au-schein-de/au-ui-library";
import { useTranslation } from "react-i18next";
import { ClickableCheckboxRow } from "../components/clickable-checkbox-row.component";
import { FilterHeader } from "../filter-header.component";
import { useFiltersContext } from "../filters.context";

const NUMBER_OF_STARS = 5;
const STAR_RATINGS = [0, 1, 2, 3, 4, 5] as const;

type StarRating = (typeof STAR_RATINGS)[number];

export function RatingFilter() {
	const { t } = useTranslation("product");
	const { filters, onFilterChange, facets } = useFiltersContext();
	const value = filters.rating ?? [];
	const facetCountsProp = facets?.rating;
	const expertReviewValue = filters.expertReview;
	const expertReviewFacetCounts = facets?.expertReview;

	const ratings = value as StarRating[];

	const getCount = (rating: StarRating) => {
		// Return undefined only if no facet data at all (initial load without Typesense)
		if (!facetCountsProp?.length) return undefined;
		// Return 0 for missing values (filtered out by current selection)
		return facetCountsProp.find((f) => f.value === String(rating))?.count ?? 0;
	};

	const handleCheckboxChange = (rating: StarRating, checked: boolean) => {
		let newRatings: StarRating[];
		if (checked) {
			newRatings = [...ratings, rating];
		} else {
			newRatings = ratings.filter((r) => r !== rating);
		}

		onFilterChange("rating", newRatings.length > 0 ? newRatings : undefined);
	};

	const getExpertReviewCount = (): number | undefined => {
		if (!expertReviewFacetCounts?.length) return undefined;
		return expertReviewFacetCounts.find((f) => f.value === "true")?.count ?? 0;
	};

	const expertReviewCount = getExpertReviewCount();
	const isExpertReviewSelected = expertReviewValue === true;
	const isExpertReviewDisabled =
		expertReviewCount === 0 && !isExpertReviewSelected;

	const activeCount = ratings.length + (isExpertReviewSelected ? 1 : 0);

	const renderStars = (rating: StarRating) =>
		Array.from({ length: NUMBER_OF_STARS }, (_, index) =>
			index < rating ? (
				// biome-ignore lint/suspicious/noArrayIndexKey: Static star icons with fixed order
				<StarIcon.Raw key={index} className="w-4 h-4 text-gray-600" />
			) : (
				// biome-ignore lint/suspicious/noArrayIndexKey: Static star icons with fixed order
				<StarOutlineIcon.Raw key={index} className="w-4 h-4 text-gray-300" />
			),
		);

	// Cannot affect checkbox size globally, must override here

	return (
		<Accordion.Item value="rating" variant="filter" data-testid="rating-filter">
			<Accordion.Trigger variant="filter" data-testid="rating-filter-trigger">
				<FilterHeader
					title={t("filters.rating")}
					value={activeCount}
					icon={<UserRatingIcon size={5} />}
				/>
			</Accordion.Trigger>
			<Accordion.Content variant="filter">
				<div className="space-y-3">
					<div className="flex items-center gap-1">
						<ClickableCheckboxRow
							size="sm"
							border="primary"
							checked={isExpertReviewSelected}
							disabled={isExpertReviewDisabled}
							onRowClick={() =>
								onFilterChange(
									"expertReview",
									isExpertReviewSelected ? undefined : true,
								)
							}
							label={
								<span className="text-body-sm">
									{t("filters.expertReview")}
								</span>
							}
							data-testid="rating-filter-expert-review"
							rowTestId="rating-filter-row-expert-review"
							rowClassName="[&_label]:text-body-sm"
							className="h-5 w-5"
						/>
						<Tooltip.Info
							info={t("filters.expertReviewInfo")}
							iconClassName="text-ansay-main"
							tooltipClassName="max-w-70 bg-white shadow-[0_4px_20px_0_#212A361A]"
							hasArrow={true}
							arrowClassName="fill-white"
						/>
						{expertReviewCount !== undefined && (
							<span className="text-gray-500 text-body-sm ml-auto">
								{expertReviewCount}
							</span>
						)}
					</div>
					{[...STAR_RATINGS].reverse().map((rating) => {
						const isSelected = ratings.includes(rating);
						const count = getCount(rating);
						// Disable if count is 0 and not already selected
						const isDisabled = count === 0 && !isSelected;

						return (
							<ClickableCheckboxRow
								key={rating}
								size="sm"
								border="primary"
								checked={isSelected}
								disabled={isDisabled}
								onRowClick={() => handleCheckboxChange(rating, !isSelected)}
								label={
									rating === 0 ? (
										<span className="text-body-sm">
											{/* @ts-ignore: dynamic key generation */}
											{t("filters.noRating")}
										</span>
									) : (
										<div className="flex items-center">
											<span className="text-body-sm w-4">{rating}</span>
											<div className="flex items-center gap-2">
												{renderStars(rating)}
											</div>
										</div>
									)
								}
								count={count}
								data-testid={`rating-filter-${rating}`}
								rowTestId={`rating-filter-row-${rating}`}
								rowClassName="[&_label]:text-body-sm"
								className="h-5 w-5"
							/>
						);
					})}
				</div>
			</Accordion.Content>
		</Accordion.Item>
	);
}
